import { EmployerSignupStatus } from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { Card } from '@zorro/design-system/v2/Card';
import {
  Stepper,
  type StepperStepProps,
} from '@zorro/design-system/v2/Stepper';
import { Title } from '@zorro/design-system/v2/Title';
import { Group } from '@zorro/web/design-system';
import {
  useEmployerFeatureFlag,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';

import { useCompanyDetailsForm } from '../../CompanyDetailsForm';
import { useCompanySetupForm } from '../../CompanySetupForm';
import { useContactsForm } from '../../ContactsForm';
import { EmployerDotsMenu } from '../../EmployerDotsMenu';
import { usePlanYearSetupForm } from '../../PlanYearSetupForm';
import { useZorroSetupForm } from '../../ZorroSetupForm';
import {
  AddEmployerStepType,
  getAddEmployerStepConfig,
} from './AddEmployerUtils';

type StepperLinkProps = StepperStepProps & {
  employerStep: AddEmployerStepType;
  isDon: boolean;
  employerId?: string;
  isValid?: boolean;
};

const StepperLink = ({
  employerStep,
  employerId = '',
  isDon,
  isValid,
  ...props
}: StepperLinkProps) => {
  const { route, label, shouldShowMissingDetails } = getAddEmployerStepConfig(
    employerStep,
    isDon
  );

  if (!employerId) {
    return <Stepper.Step {...props} label={label} />;
  }

  const stepIconProps = {
    isPartial: !isValid && !shouldShowMissingDetails,
    isPartialAndRequired: !isValid && shouldShowMissingDetails,
  };

  return (
    <Stepper.Link
      {...props}
      {...stepIconProps}
      href={APP_ROUTES[route](employerId)}
      label={label}
    />
  );
};

type Props = {
  isFinalizationMode: boolean;
  isSignedAgreementVisible: boolean;
  employerStep: AddEmployerStepType;
  isDon: boolean;
  employerId?: string;
};

export const AddEmployerStepper = ({
  employerStep,
  employerId,
  isFinalizationMode,
  isSignedAgreementVisible,
  isDon,
}: Props) => {
  const { isOn: isLynxPaymentsEnabled } = useEmployerFeatureFlag(
    'lynx_payments_enabled',
    employerId
  );
  const { isZorroOperations } = useRoles();

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { stepIndex } = getAddEmployerStepConfig(employerStep, isDon);
  const { companyDetailsForm } = useCompanyDetailsForm({
    employerId,
    isFinalizationMode,
    isSignedAgreementVisible,
    isDon,
  });
  const { contactsForm } = useContactsForm(employerId, isFinalizationMode);
  const { companySetupForm } = useCompanySetupForm(
    employerId,
    isFinalizationMode
  );
  const { zorroSetupForm } = useZorroSetupForm({ employerId });
  const { planYearSetupForm } = usePlanYearSetupForm({
    shouldFallbackToEarliestPeriod: true,
    isFinalizationMode,
    employerId,
  });

  const areAllFormsValid =
    companyDetailsForm.formState.isValid &&
    contactsForm.formState.isValid &&
    companySetupForm.formState.isValid &&
    zorroSetupForm.formState.isValid &&
    planYearSetupForm.formState.isValid;

  const isReviewFormValid =
    isLynxPaymentsEnabled &&
    employer?.signupStatus === EmployerSignupStatus.IN_ZORRO_SETUP &&
    areAllFormsValid;

  const title = isDon ? 'Complete your company profile' : 'Add new employer';

  return (
    <Card variant="grey" style={{ borderRadius: 8 }}>
      <Group>
        <Title>{title}</Title>
        {employerId && !isDon && <EmployerDotsMenu employerId={employerId} />}
      </Group>
      <Stepper
        allowNextStepsSelect={false}
        active={stepIndex}
        wrap={false}
        size="sm"
        mt="lg"
      >
        <StepperLink
          isValid={companyDetailsForm.formState.isValid}
          employerStep={AddEmployerStepType.COMPANY_DETAILS}
          employerId={employerId}
          isDon={isDon}
        />
        <StepperLink
          isValid={contactsForm.formState.isValid}
          employerStep={AddEmployerStepType.CONTACTS}
          employerId={employerId}
          isDon={isDon}
        />
        <StepperLink
          isValid={companySetupForm.formState.isValid}
          employerStep={AddEmployerStepType.COMPANY_SETUP}
          employerId={employerId}
          isDon={isDon}
        />
        {!isDon && (
          <StepperLink
            isValid={zorroSetupForm.formState.isValid}
            employerStep={AddEmployerStepType.ZORRO_SETUP}
            employerId={employerId}
            isDon={isDon}
          />
        )}
        {!isDon && (
          <StepperLink
            isValid={planYearSetupForm.formState.isValid}
            employerStep={AddEmployerStepType.PLAN_YEAR_SETUP}
            employerId={employerId}
            isDon={isDon}
          />
        )}
        <StepperLink
          isValid={isReviewFormValid}
          employerStep={AddEmployerStepType.REVIEW}
          employerId={employerId}
          isDon={isDon}
        />
        {isZorroOperations && isLynxPaymentsEnabled && (
          <StepperLink
            employerStep={AddEmployerStepType.FINALIZE}
            employerId={employerId}
            isDon={isDon}
          />
        )}
      </Stepper>
    </Card>
  );
};
